
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { 
  MessageCircle, Send, Search, Users, ChevronLeft, 
  MoreVertical, Smile, Paperclip, Loader2, Sparkles,
  User as UserIcon, Library, Trash2, X, BookOpen
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { useData } from '../contexts/DataContext';
import { chatService } from '../services/chatService';
import { bookService } from '../services/bookService';
import BookDetailsModal from './BookDetailsModal';
import { useMention } from '../hooks/useMention';
import MentionDropdown from '../components/MentionDropdown';
import { AppBackButton } from '../components/AppBackButton';
import { AnimatedIcon } from '../components/AnimatedIcon';
import { CommunityLinks } from '../components/CommunityLinks';
import { StudyPageHeader } from '../components/StudyPageHeader';

const ChatPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { isDarkMode } = useTheme();
  const { friends } = useData();
  const [activeChat, setActiveChat] = useState<any>(null);
  
  const {
      text: message,
      setText: setMessage,
      handleKeyDown,
      handleSelectMention,
      handleCursorChange,
      mentionResults,
      selectedIndex,
      showDropdown
  } = useMention('', friends);

  const [query, setQuery] = useState(''); // Search input
  const [searchQuery, setSearchQuery] = useState(''); // Debounced query
  const [messages, setMessages] = useState<any[]>([]);
  const [chatId, setChatId] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const lastFetchedChatRef = useRef<string | null>(null); // Lock to prevent infinite 429 loops

  // Menu and Modal States
  const [showMenu, setShowMenu] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [friendBooks, setFriendBooks] = useState<any[]>([]);
  const [loadingBooks, setLoadingBooks] = useState(false);
  
  // Book Detail Modal States
  const [selectedBookForDetails, setSelectedBookForDetails] = useState<any | null>(null);
  const [isImporting, setIsImporting] = useState(false);

  // Dialog States
  const [confirmDelete, setConfirmDelete] = useState(false);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Handle Initial Friend from State
  useEffect(() => {
    const state = location.state as { friendId?: string };
    if (state?.friendId && friends.length > 0) {
      const friend = friends.find(f => f.id === state.friendId);
      if (friend) setActiveChat(friend);
    }
  }, [location.state, friends]);

  // Load History
  useEffect(() => {
    if (activeChat && user && !user.isGuest) {
      // Prevent redundant fetches if this chat is already being loaded/loaded
      if (lastFetchedChatRef.current === activeChat.id) return;
      lastFetchedChatRef.current = activeChat.id;

      chatService.getOrCreateChat(user.id, activeChat.id).then(id => {
        setChatId(id);
        chatService.getMessages(id).then(msgs => {
          setMessages(msgs.map((m: any) => ({
            id: m.id,
            text: m.content,
            sender: m.sender_id === user.id ? 'me' : 'them',
            timestamp: new Date(m.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
          })));
        });
      }).catch(err => {
        console.error("[ChatPage] Error loading chat:", err);
        lastFetchedChatRef.current = null; // Allow retry on error
      });
    } else {
      setChatId(null);
      setMessages([]);
      lastFetchedChatRef.current = null;
    }
  }, [activeChat, user]);

  // Foreground Realtime FCM Reactive Push
  useEffect(() => {
    const handlePush = (e: any) => {
      const payload = e.detail;
      if (payload?.data?.chat_id === chatId) {
        const newMsg = {
          id: Date.now().toString(),
          text: payload.notification?.body,
          sender: 'them',
          timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
        };
        setMessages(prev => [...prev, newMsg]);
      }
    };
    window.addEventListener('libritui-new-message', handlePush);
    return () => window.removeEventListener('libritui-new-message', handlePush);
  }, [chatId]);

  // Handle Click Outside Menu
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setShowMenu(false);
      }
    };
    if (showMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showMenu]);

  // 500ms Debounce for Contacts Search
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchQuery(query);
    }, 500);
    return () => clearTimeout(timer);
  }, [query]);

  const filteredFriends = useMemo(() => {
    if (!searchQuery.trim()) return friends;
    const q = searchQuery.toLowerCase();
    return friends.filter(f => 
      f.display_name.toLowerCase().includes(q) || 
      f.username.toLowerCase().includes(q)
    );
  }, [friends, searchQuery]);

  const handleOpenProfile = async () => {
    if (!activeChat) return;
    setShowMenu(false);
    setShowProfileModal(true);
    setLoadingBooks(true);
    setFriendBooks([]);

    try {
        const response = await bookService.getBooks(activeChat.id);
        const data = response.data?.filter((b: any) => b.category?.toLowerCase().includes('libritui library'));

        const mappedBooks = (data || []).map(b => ({
            id: b.custom_id?.split('-')[1] || b.custom_id || '',
            title: b.title,
            author: b.author,
            language: b.language || 'und',
            format: b.file_type || 'html',
            cover: b.cover_url,
            file: b.file_url,
            progress: b.last_page ? 50 : 0
        }));

        setFriendBooks(mappedBooks);
    } catch (err) {
        console.error("Error fetching friend books:", err);
    } finally {
        setLoadingBooks(false);
    }
  };

  const handleDeleteChat = async () => {
    if (!chatId) return;
    try {
        await chatService.deleteChat(chatId);
        setMessages([]);
        setChatId(null);
        setActiveChat(null);
        setConfirmDelete(false);
    } catch (err) {
        console.error("Error deleting chat:", err);
        alert(t('hubs.community.chat.errorDelete', 'Erro ao excluir conversa.'));
    }
  };

  const handleImportSharedBook = async (book: any) => {
    if (!user || user.isGuest) return;
    setIsImporting(true);
    try {
        const bookId = `gut-${book.id}-${Date.now()}`;
        const mimeType = book.format === 'html' ? 'text/html' : 'application/pdf';

        const { storageService } = await import('../services/storageService');
        storageService.saveBook({
            id: bookId, title: book.title, author: book.author,
            coverUrl: book.cover || `generated://${bookId}`,
            fileUrl: book.file,
            lastPage: 1, totalPages: 0, dateAdded: Date.now(),
            category: `LibriTui Library (${book.format.toUpperCase()})`, isFavorite: false, language: book.language,
            format: book.format as any
        });

        localStorage.setItem(`mime_${bookId}`, mimeType);

        await bookService.saveBook(user.id, {
            id: bookId,
            custom_id: bookId,
            title: book.title,
            author: book.author,
            file_url: book.file,
            cover_url: book.cover || `generated://${bookId}`,
            last_page: 1,
            user_id: user.id,
            file_type: book.format,
            language: book.language,
            category: book.format === 'html' ? 'LibriTui Library (HTML)' : 'LibriTui Library (PDF)'
        });

        setSelectedBookForDetails(null);
        setShowProfileModal(false);
        navigate(`/reader/${bookId}`);
    } catch (err) {
        console.error("Import error:", err);
        alert(t('hubs.community.chat.errorImport', 'Erro ao importar livro compartilhado.'));
    } finally {
        setIsImporting(false);
    }
  };

  const handleSendMessage = async () => {
    if (!message.trim() || !chatId || !user) return;
    
    const msgText = message;
    setMessage('');
    
    // Optimistic UI Append
    const tempMsg = {
      id: Date.now().toString(),
      text: msgText,
      sender: 'me',
      timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    };
    setMessages(prev => [...prev, tempMsg]);

    try {
      await chatService.sendMessage(chatId, user.id, msgText);
    } catch(e) {
      console.error("Failed to send message to D1", e);
      // Rollback is usually better, but for MVP it's visually persisted immediately natively
    }
  };

  return (
    <div className="max-w-6xl mx-auto space-y-6 pb-24 animate-in fade-in slide-in-from-bottom-6 duration-700">
      <div className="space-y-4">
          <AppBackButton fallbackTo="/" />
          <StudyPageHeader
              tourKey="chat"
              title={t('hubs.community.chat.title', 'Conversas')}
              actions={
                <>
                <CommunityLinks current="chat" />
                </>
              }
          />
      </div>

      <div className="bg-surface rounded-[32px] border border-black/[0.06] dark:border-white/[0.06] overflow-hidden flex h-[72vh]">
        
        {/* Sidebar - Contacts */}
        <div data-tour="chat-contacts" className="w-80 border-r border-black/[0.06] dark:border-white/[0.06] flex flex-col bg-tint-stone/40">
          <div className="p-8 border-b border-black/[0.06] dark:border-white/[0.06]">
            <p className="font-mono text-[10px] uppercase tracking-[0.16em] text-muted flex items-center gap-2">
              <MessageCircle size={13} strokeWidth={1.75} />
              {t('hubs.community.chat.title', 'Conversas')}
            </p>
            <div className="mt-6 relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-muted" size={16} />
              <input 
                type="text" 
                placeholder={t('hubs.community.chat.searchPlaceholder', 'Buscar contatos...')}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                className="w-full bg-surface py-3 pl-12 pr-4 rounded-2xl text-xs font-bold outline-none focus:ring-2 focus:ring-primary/50 transition-all border-none"
              />
            </div>
          </div>

          <div className="flex-1 overflow-y-auto custom-scrollbar p-4 space-y-2">
            {filteredFriends.length === 0 ? (
              <div className="flex flex-col items-center justify-center p-8 text-center space-y-4 h-full opacity-40">
                <Users size={32} />
                <p className="text-[10px] font-normal uppercase font-mono tracking-[0.14em] leading-relaxed">
                  {query ? t('hubs.community.chat.noContactsFound', 'Nenhum contato encontrado') : t('hubs.community.chat.addFriendsToStart', 'Adicione amigos para começar a conversar')}
                </p>
              </div>
            ) : (
              filteredFriends.map((friend: any) => (
                <button
                  key={friend.id}
                  onClick={() => setActiveChat(friend)}
                  className={`w-full flex items-center gap-4 p-4 rounded-[24px] transition-all hover:scale-[1.02] active:scale-95 ${activeChat?.id === friend.id ? 'bg-surface ' : 'hover:bg-white/50 '}`}
                >
                  <div className="w-12 h-12 rounded-2xl overflow-hidden border border-primary/10 flex-shrink-0">
                    <img 
                      src={friend.avatar_url || `https://api.dicebear.com/7.x/initials/svg?seed=${friend.display_name}&backgroundColor=b0805f,7d9278,8a85a8,b39a64,6f93a6&textColor=fbf8f3`} 
                      className="w-full h-full object-cover"
                      alt=""
                    />
                  </div>
                  <div className="flex-1 text-left min-w-0">
                    <h3 className="font-semibold text-ink text-sm truncate">{friend.display_name}</h3>
                    <p className="text-[10px] font-bold text-muted truncate">@{friend.username}</p>
                  </div>
                  {Math.random() > 0.7 && (
                    <div className="w-2 h-2 bg-ink rounded-full" />
                  )}
                </button>
              ))
            )}
          </div>
        </div>

        {/* Chat Area */}
        {activeChat ? (
          <div data-tour="chat-area" className="flex-1 flex flex-col bg-surface">
            {/* Chat Header */}
            <div className="p-6 border-b border-black/[0.06] dark:border-white/[0.06] flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-xl overflow-hidden border border-primary/20 bg-tint-stone/40">
                  <img 
                    src={activeChat.avatar_url || `https://api.dicebear.com/7.x/initials/svg?seed=${activeChat.display_name}&backgroundColor=b0805f,7d9278,8a85a8,b39a64,6f93a6&textColor=fbf8f3`} 
                    className="w-full h-full object-cover"
                    alt=""
                  />
                </div>
                <div>
                  <h3 className="font-semibold text-ink text-sm">{activeChat.display_name}</h3>
                  <div className="flex items-center gap-1.5">
                    <div className="w-1.5 h-1.5 bg-ok rounded-full animate-pulse" />
                    <span className="text-[9px] font-normal uppercase font-mono text-ok tracking-[0.14em]">{t('hubs.community.chat.online', 'Online')}</span>
                  </div>
                </div>
              </div>
              <div className="relative" ref={menuRef}>
                <button 
                  onClick={() => setShowMenu(!showMenu)}
                  className={`p-2 hover:bg-tint-stone/60 rounded-xl transition-all ${showMenu ? 'text-primary bg-primary/5' : 'text-muted'}`}
                >
                  <MoreVertical size={20} />
                </button>

                {showMenu && (
                  <div className="absolute right-0 mt-2 w-56 bg-surface rounded-2xl shadow-[0_24px_60px_-24px_rgba(0,0,0,0.35)] border border-black/[0.06] dark:border-white/[0.06] py-3 z-50 animate-in fade-in zoom-in-95 duration-200">
                    <button 
                      onClick={handleOpenProfile}
                      className="w-full flex items-center gap-3 px-5 py-3 hover:bg-tint-stone/40 transition-colors text-left group"
                    >
                      <UserIcon size={18} className="text-muted group-hover:text-primary transition-colors" />
                      <span className="text-sm font-bold text-ink group-hover:text-primary transition-colors">{t('hubs.community.chat.openProfile', 'Abrir perfil')}</span>
                    </button>
                    <div className="h-px bg-tint-stone/40 my-2 mx-5" />
                    <button 
                      onClick={() => { setShowMenu(false); setConfirmDelete(true); }}
                      className="w-full flex items-center gap-3 px-5 py-3 hover:bg-tint-rose dark:hover:bg-tint-rose/20 transition-colors text-left group"
                    >
                      <Trash2 size={18} className="text-muted group-hover:text-bad transition-colors" />
                      <span className="text-sm font-bold text-ink group-hover:text-bad transition-colors">{t('hubs.community.chat.deleteChat', 'Excluir conversa')}</span>
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-8 space-y-6 custom-scrollbar bg-tint-stone/30">
              <div className="text-center">
                <span className="inline-block px-4 py-1.5 bg-tint-stone/60 text-muted text-[9px] font-medium rounded-full">{t('hubs.community.chat.today', 'Hoje')}</span>
              </div>

              {messages.map((msg) => (
                <div key={msg.id} className={`flex ${msg.sender === 'me' ? 'justify-end' : 'justify-start'} animate-in fade-in zoom-in-95 duration-300`}>
                  <div className={`max-w-[70%] space-y-1 ${msg.sender === 'me' ? 'items-end' : 'items-start'} flex flex-col`}>
                    <div className={`px-6 py-4 rounded-[1.5rem] text-sm font-medium ${
                      msg.sender === 'me' 
                        ? 'bg-ink text-surface rounded-tr-sm' 
                        : 'bg-surface text-ink border border-black/[0.06] dark:border-white/[0.06] rounded-tl-sm'
                    }`}>
                      {msg.text}
                    </div>
                    <span className="text-[9px] font-bold text-muted px-1">{msg.timestamp}</span>
                  </div>
                </div>
              ))}
              <div ref={messagesEndRef} />
            </div>

            {/* Chat Input */}
            <div className="p-6 bg-surface border-t border-black/[0.06] dark:border-white/[0.06]">
              <div className="flex items-center gap-3 bg-tint-stone/60 p-2 rounded-[24px] border border-transparent focus-within:border-primary/20 transition-all">
                <button className="p-3 text-muted hover:text-primary transition-all">
                  <Smile size={20} />
                </button>
                <button className="p-3 text-muted hover:text-primary transition-all">
                  <Paperclip size={20} />
                </button>
                <div className="relative flex-1">
                  <input 
                    type="text" 
                    value={message}
                    onChange={(e) => {
                        setMessage(e.target.value);
                        handleCursorChange(e.target.selectionStart || 0);
                    }}
                    onKeyDown={(e) => {
                        if (mentionResults.length > 0) {
                            handleKeyDown(e);
                        } else if (e.key === 'Enter') {
                            handleSendMessage();
                        }
                    }}
                    onKeyUp={(e) => handleCursorChange((e.target as HTMLInputElement).selectionStart || 0)}
                    onMouseUp={(e) => handleCursorChange((e.target as HTMLInputElement).selectionStart || 0)}
                    placeholder={t('hubs.community.chat.inputPlaceholder', 'Escreva sua mensagem...')}
                    className="w-full bg-transparent px-2 py-2 text-sm font-medium dark:text-ink outline-none border-none"
                  />
                  <MentionDropdown
                      results={mentionResults}
                      selectedIndex={selectedIndex}
                      onSelect={handleSelectMention}
                  />
                </div>
                <button 
                  onClick={handleSendMessage}
                  disabled={!message.trim()}
                  className={`p-4 rounded-full transition-all active:scale-95 ${
                    message.trim() 
                      ? 'bg-ink text-surface rotate-0' 
                      : 'bg-tint-stone text-muted opacity-50'
                  }`}
                >
                  <Send size={18} />
                </button>
              </div>
            </div>
          </div>
        ) : (
          <div data-tour="chat-area" className="flex-1 flex flex-col items-center justify-center p-12 text-center bg-surface relative overflow-hidden">
            <div className="absolute top-0 left-0 w-full h-full opacity-[0.03] pointer-events-none">
              <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] bg-ink rounded-full blur-[120px]" />
              <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] bg-secondary rounded-full blur-[120px]" />
            </div>

            <div className="relative z-10 space-y-6 max-w-sm">
              <div className="w-20 h-20 bg-tint-rose rounded-full flex items-center justify-center text-ink mx-auto mb-6">
                <AnimatedIcon motion="wobble" active><MessageCircle size={30} strokeWidth={1.5} /></AnimatedIcon>
              </div>
              <h2 className="font-display text-3xl font-light text-ink tracking-[-0.04em]">{t('hubs.community.chat.emptyTitle', 'Suas Conversas')}</h2>
              <p className="text-muted font-medium leading-relaxed italic">
                {t('hubs.community.chat.emptyDesc', 'Selecione um amigo na lista ao lado para iniciar um bate-papo ou compartilhar suas leituras no LibriTui.')}
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Profile Modal */}
      {showProfileModal && activeChat && (
        <div className="fixed inset-0 z-[120] flex items-center justify-center p-4 bg-black/30 backdrop-blur-md animate-in fade-in duration-300">
          <div className="bg-surface w-full max-w-2xl rounded-[28px] overflow-hidden animate-in zoom-in-95 relative flex flex-col border border-black/[0.06] dark:border-white/[0.06]" style={{ maxHeight: '85vh' }}>
            <button onClick={() => setShowProfileModal(false)} className="absolute top-6 right-6 p-3 bg-tint-stone/60 rounded-full text-muted hover:bg-tint-stone transition-all z-10">
              <X size={20} />
            </button>
            <div className="p-8 pb-4 flex flex-col items-center">
              <div className="w-24 h-24 rounded-[24px] overflow-hidden border-4 border-primary/20 mb-4 flex items-center justify-center bg-tint-stone/40">
                <img src={activeChat.avatar_url || `https://api.dicebear.com/7.x/initials/svg?seed=${activeChat.display_name}&backgroundColor=b0805f,7d9278,8a85a8,b39a64,6f93a6&textColor=fbf8f3`} className="w-full h-full object-cover" alt="" />
              </div>
              <h2 className="text-2xl font-medium dark:text-ink">{activeChat.display_name}</h2>
              <p className="text-xs font-mono font-semibold text-primary mt-1">@{activeChat.username}</p>
              <div className="mt-4 text-center max-w-md">
                <p className="text-sm text-muted font-medium italic">{activeChat.bio || t('hubs.community.chat.noBio', 'Nenhuma bio definida.')}</p>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto px-8 pb-8 custom-scrollbar">
              <div className="mt-8">
                <div className="flex items-center gap-3 mb-6">
                  <Library size={18} className="text-primary" />
                  <h3 className="text-xs font-normal uppercase font-mono tracking-[0.14em] text-muted">{t('hubs.community.chat.friendShelf', 'Estante do Amigo')}</h3>
                </div>
                {loadingBooks ? (
                  <div className="flex items-center justify-center py-12"><Loader2 className="animate-spin text-primary" size={32} /></div>
                ) : friendBooks.length === 0 ? (
                  <div className="bg-tint-stone/40 p-10 rounded-[24px] border border-dashed border-black/[0.06] dark:border-white/[0.06] text-center">
                    <p className="text-sm text-muted font-medium">{t('hubs.community.chat.emptyShelf', 'Este amigo ainda não salvou livros da biblioteca online.')}</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-6">
                    {friendBooks.map(book => (
                      <div key={book.id} onClick={() => setSelectedBookForDetails(book)} className="group cursor-pointer space-y-3">
                        <div className="aspect-[3/4] rounded-2xl overflow-hidden bg-tint-stone group-hover:scale-105 transition-all relative">
                          <BookCoverSmall title={book.title} author={book.author} coverUrl={book.cover} />
                          <div className="absolute inset-x-0 bottom-0 h-1 bg-surface/40">
                            <div className="h-full bg-ink" style={{ width: `${book.progress}%` }} />
                          </div>
                        </div>
                        <div className="px-1">
                          <h4 className="text-[10px] font-normal dark:text-ink line-clamp-1 uppercase font-mono tracking-[0.14em]">{book.title}</h4>
                          <p className="text-[8px] font-bold text-muted mt-0.5 line-clamp-1">{book.author}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Book Details Modal */}
      {selectedBookForDetails && (
        <BookDetailsModal
          book={selectedBookForDetails}
          onClose={() => setSelectedBookForDetails(null)}
          onImport={handleImportSharedBook}
          onPreview={() => window.open(selectedBookForDetails.file, '_blank')}
          isImporting={isImporting}
          languageFlags={{ 'en': '🇺🇸', 'pt': '🇧🇷', 'es': '🇪🇸', 'fr': '🇫🇷', 'de': '🇩🇪', 'it': '🇮🇹', 'zh': '🇨🇳', 'ja': '🇯🇵' }}
          renderCover={(b) => (
            <div className="w-full h-full bg-tint-stone/60">
               <BookCoverSmall title={selectedBookForDetails.title} author={selectedBookForDetails.author} coverUrl={selectedBookForDetails.cover} />
            </div>
          )}
        />
      )}

      {/* Delete Confirmation Dialog */}
      {confirmDelete && (
        <div className="fixed inset-0 z-[300] flex items-center justify-center p-6 bg-black/30 backdrop-blur-sm animate-in fade-in duration-200">
          <div className="bg-surface w-full max-w-sm rounded-[24px] p-8 border border-black/[0.06] dark:border-white/[0.06] animate-in zoom-in-95 duration-200">
            <h3 className="text-xl font-medium text-ink tracking-[-0.02em] mb-2 text-center">{t('hubs.community.chat.deleteConfirmTitle', 'Excluir Conversa?')}</h3>
            <p className="text-muted font-bold text-sm mb-8 leading-relaxed text-center">
              {t('hubs.community.chat.deleteConfirmDesc', 'Tem certeza que deseja apagar todo o histórico de mensagens com desta conversa? Esta ação não pode ser desfeita.')}
            </p>
            <div className="flex gap-3">
              <button onClick={() => setConfirmDelete(false)} className="flex-1 py-4 px-6 border border-black/[0.06] dark:border-white/[0.06] rounded-2xl font-medium text-[10px] text-muted hover:bg-tint-stone/40 transition-all">
                {t('common.cancel', 'Cancelar')}
              </button>
              <button onClick={handleDeleteChat} className="flex-1 py-4 px-6 bg-bad text-surface rounded-2xl font-medium text-[10px] hover:scale-[1.02] active:scale-95 transition-all">
                {t('hubs.community.chat.deleteConfirmBtn', 'Sim, Excluir')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const BookCoverSmall: React.FC<{ title: string; author?: string; coverUrl?: string }> = ({ title, author, coverUrl }) => {
  const [hasError, setHasError] = useState(false);
  const { isDarkMode } = useTheme();

  if (coverUrl && !hasError) {
    return <img src={coverUrl} alt={title} className="w-full h-full object-cover rounded-xl" onError={() => setHasError(true)} />;
  }

  return (
    <div className={`w-full h-full rounded-xl flex flex-col relative overflow-hidden p-3 ${isDarkMode ? 'bg-surface ' : 'bg-surface '}`}>
      <div className="absolute inset-0 flex items-center justify-center opacity-10"><BookOpen size={40} /></div>
      <div className="mt-auto relative z-10">
        <h4 className="text-[10px] font-normal leading-tight line-clamp-2 uppercase tracking-[0.14em] font-mono">{title}</h4>
        {author && <p className="text-[8px] font-bold text-muted mt-1 line-clamp-1">{author}</p>}
      </div>
    </div>
  );
};

export default ChatPage;
